<script setup lang="tsx">
import { ref } from 'vue'
import http from '@/services/http'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useModuleColumns from './columns'
import columnStore from '@/stores/column'
import type { PageRequest, PageResponse, Response, ModuleMeta } from '@/models/api-generated'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import ModuleFilters from './ModuleFilters.vue'
import { getResponsibleIcon } from '@/services/utils'
import { getStatus, getStatusIndicator, moduleRefreshCallback } from './shared'
import sliderStore from '@/stores/slider'
import type { ModuleFilter } from '@/models/filters-generated'
import MethodStats from './MethodStats.vue'

const props = defineProps({
	component: { default: 'modules' },
})

const slider = sliderStore()
const model = defineModel<PageResponse<ModuleMeta>>()
const isLoading = ref(false)
const { columns } = useModuleColumns(props.component)
const root = columnStore().defineComponent(props.component, fetch)
moduleRefreshCallback.value = root.triggerFetch

async function fetch(signal: AbortSignal) {
	const request: PageRequest<ModuleFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		filter: root.filterValue() as ModuleFilter,
		orderBy: root.orderValue(),
	}

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<ModuleMeta>>>(`api/module/list`, request, { signal })
	isLoading.value = false

	if (data.data) {
		model.value = data.data
	}
}
</script>

<template>
	<ModuleFilters :component="props.component"></ModuleFilters>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table" data-test="module-table">
				<thead>
					<tr>
						<th v-if="columns.description.value.isVisible">
							<OrderLabel v-model="columns.description.value">{{ $t('description') }}</OrderLabel>
						</th>
						<th v-if="columns.code.value.isVisible">
							<OrderLabel v-model="columns.code.value">{{ $t('code') }}</OrderLabel>
						</th>
						<th v-if="columns.type.value.isVisible">
							<OrderLabel v-model="columns.type.value">{{ $t('module.type') }}</OrderLabel>
						</th>
						<th v-if="columns.active.value.isVisible">
							<OrderLabel v-model="columns.active.value">{{ columns.active.value.label }}</OrderLabel>
						</th>
						<th v-if="columns.stats.value.isVisible">
							{{ $t('module.stats') }}
						</th>
						<th v-if="columns.responsible.value.isVisible">
							<OrderLabel v-model="columns.responsible.value">{{ $t('responsible') }}</OrderLabel>
						</th>
						<th v-if="columns.instance.value.isVisible">
							<OrderLabel v-model="columns.instance.value">{{ $t('institution') }}</OrderLabel>
						</th>
						<th v-if="columns.version.value.isVisible">
							<OrderLabel v-model="columns.version.value">{{ $t('version') }}</OrderLabel>
						</th>
						<th>
							{{ $t('relatedAssets') }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(meta, i) in model?.items"
						@mouseup.stop="slider.openViewModule(meta.id, meta.module.name, meta.module.isCustom)"
						:key="meta.id"
						data-test="module-row"
						:data-test-id="meta.module.id"
						class="module-row"
						:class="{ odd: i % 2 != 0 }"
					>
						<td v-if="columns.description.value.isVisible">
							{{ meta.module.description }}
						</td>
						<td v-if="columns.code.value.isVisible">
							{{ meta.module.name }}
						</td>
						<td v-if="columns.type.value.isVisible">
							{{ meta.module.type }}
						</td>
						<td v-if="columns.active.value.isVisible">
							<component :is="getStatusIndicator(getStatus(meta.active, meta.isApplicable))" />
						</td>
						<td v-if="columns.stats.value.isVisible">
							<MethodStats v-if="meta.isApplicable" :module-meta-id="meta.id" />
						</td>
						<td v-if="columns.responsible.value.isVisible">
							<i v-if="meta.responsible" :class="getResponsibleIcon(meta.responsible)" alt="responsible"></i>
							{{ meta.responsible ? meta.responsible.label : '-' }}
						</td>
						<td class="text-nowrap" v-if="columns.instance.value.isVisible">
							{{ meta.instance.name }}
						</td>
						<td v-if="columns.version.value.isVisible">
							<template v-if="meta.module.version">
								{{ $te(meta.module.version) ? $t(meta.module.version) : meta.module.version }}
							</template>
							<template v-else> - </template>
						</td>
						<td>
							{{ meta.numAssets }}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</LoadingContainer>
	<Pagination v-model="root.pagination" :total-pages="model?.pages" />
</template>

<style lang="sass" scoped>
td:hover
    cursor: pointer
</style>
